// Spec rev150 (2026-02-07) — helper partagé pour l'icône + couleurs
// d'une pièce jointe selon son extension. Utilisé par tous les
// composants qui affichent des attachments (Documents, Tâches, AVI,
// Chat, historique, etc.) pour garantir une cohérence visuelle.
//
// Chaque catégorie retourne : { Icon, iconColor, bg, label, isImage }
// où Icon est un composant lucide-react à rendre en 18px par défaut,
// iconColor est la teinte de l'icône, bg est le fond du badge carré
// 36x36 (border-radius 8px). isImage=true déclenche un rendu miniature
// à la place de l'icône chez les consommateurs qui le supportent.
import {
  FileText, FileSpreadsheet, Image as ImageIcon, FileArchive, File as FileGeneric,
} from "lucide-react";

const PDF = { Icon: FileText, iconColor: "#e07a7a", bg: "#3a1414", label: "PDF" };
const XLS = { Icon: FileSpreadsheet, iconColor: "#5ab87a", bg: "#0f2818", label: "Tableur" };
const DOC = { Icon: FileText, iconColor: "#5aa8e0", bg: "#101e2e", label: "Word" };
const IMG = { Icon: ImageIcon, iconColor: "#c99aeb", bg: "#2a1a3a", label: "Image", isImage: true };
const ZIP = { Icon: FileArchive, iconColor: "#e0a55a", bg: "#2a2410", label: "Archive" };
const GEN = { Icon: FileGeneric, iconColor: "#8b8b90", bg: "#232327", label: "Fichier" };

const BY_EXT = {
  pdf: PDF,
  xls: XLS, xlsx: XLS, xlsm: XLS, csv: XLS, tsv: XLS, ods: XLS,
  doc: DOC, docx: DOC, odt: DOC, rtf: DOC,
  jpg: IMG, jpeg: IMG, png: IMG, gif: IMG, webp: IMG, bmp: IMG,
  svg: IMG, heic: IMG, heif: IMG, tif: IMG, tiff: IMG,
  zip: ZIP, rar: ZIP, "7z": ZIP, tar: ZIP, gz: ZIP,
};

/**
 * Retourne les métadonnées visuelles d'une pièce jointe.
 * @param {string|object} nameOrAttachment - nom du fichier ou objet
 *   attachment { file_name?, name?, mime? }.
 * @returns {{Icon: Function, iconColor: string, bg: string, label: string, isImage?: boolean, ext: string}}
 */
export function getFileTypeMeta(nameOrAttachment) {
  const name = typeof nameOrAttachment === "string"
    ? nameOrAttachment
    : (nameOrAttachment?.file_name || nameOrAttachment?.name || nameOrAttachment?.filename || "");
  const ext = (name.split(".").pop() || "").toLowerCase().trim();
  const meta = BY_EXT[ext] || GEN;
  return { ...meta, ext };
}

/**
 * Rend le badge d'icône type-fichier standard 36x36 avec bord arrondi 8px.
 * @param {object} props - { name, size = 36, iconSize = 18 }
 * @returns {JSX.Element}
 */
export function FileTypeBadge({ name, size = 36, iconSize = 18, className = "" }) {
  const { Icon, iconColor, bg } = getFileTypeMeta(name);
  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        background: bg,
        borderRadius: 8,
        color: iconColor,
      }}
      data-testid="file-type-badge"
    >
      <Icon size={iconSize} strokeWidth={2} />
    </span>
  );
}
